import { cva } from 'class-variance-authority';

import { cn } from '@/shared/utils';
import { Icon, IconKey } from '../icon';
import { Field, FieldDescription, FieldError, FieldLabel } from '../ui/field';

// ─── Public types ──────────────────────────────────────────────

export interface CardToggleOption<T> {
  value: T;
  label: string;
  description?: string;
  icon: IconKey;
}

export interface FieldCardToggleProps<T extends string> {
  label?: string;
  description?: string;
  errors?: Array<{ message?: string } | undefined>;
  required?: boolean;
  disabled?: boolean;
  options: CardToggleOption<T>[];
  value: T;
  onValueChange: (value: T) => void;
  variant?: 'horizontal' | 'vertical' | 'vertical-prominent';
  colorScheme?: 'primary' | 'accent';
  columns?: number;
  className?: string;
}

// ─── Variant styles ────────────────────────────────────────────

const cardToggleVariants = cva(
  'relative flex cursor-pointer items-center transition-colors disabled:cursor-not-allowed disabled:opacity-50',
  {
    variants: {
      variant: {
        horizontal: 'gap-3 rounded-lg border-2 p-3 text-left',
        vertical: 'flex-col gap-1.5 rounded-lg border px-3 py-2.5 text-xs font-medium',
        'vertical-prominent': 'flex-col gap-1.5 rounded-xl border-2 p-3 text-center cursor-pointer hover:shadow-sm',
      },
      colorScheme: {
        primary: '',
        accent: '',
      },
      active: {
        true: '',
        false: '',
      },
    },
    compoundVariants: [
      // primary + active
      { colorScheme: 'primary', active: true, className: 'border-primary bg-primary/5' },
      { colorScheme: 'primary', active: false, className: 'border-muted hover:border-muted-foreground/30' },
      // accent + active (vertical)
      { colorScheme: 'accent', variant: 'vertical', active: true, className: 'border-accent bg-accent/10 text-accent' },
      { colorScheme: 'accent', variant: 'vertical', active: false, className: 'border-border bg-background text-muted-foreground hover:bg-muted/50' },
      // accent + active (vertical-prominent)
      { colorScheme: 'accent', variant: 'vertical-prominent', active: true, className: 'border-accent bg-accent/8 shadow-sm' },
      { colorScheme: 'accent', variant: 'vertical-prominent', active: false, className: 'border-border bg-card hover:border-accent/40' },
    ],
    defaultVariants: {
      variant: 'horizontal',
      colorScheme: 'primary',
      active: false,
    },
  }
);

// ─── Helpers ───────────────────────────────────────────────────

/** Maps column count to a static Tailwind grid class (avoids JIT purge issues). */
function gridColsClass(cols: number): string {
  switch (cols) {
    case 1: return 'grid-cols-1';
    case 2: return 'grid-cols-2';
    case 3: return 'grid-cols-3';
    case 4: return 'grid-cols-4';
    default: return 'grid-cols-3';
  }
}

// ─── Sub-components ────────────────────────────────────────────

interface CardToggleButtonProps<T extends string> {
  option: CardToggleOption<T>;
  active: boolean;
  disabled?: boolean;
  variant: 'horizontal' | 'vertical' | 'vertical-prominent';
  colorScheme: 'primary' | 'accent';
  onClick: (value: T) => void;
}

function CardToggleButton<T extends string>({
  option,
  active,
  disabled,
  variant,
  colorScheme,
  onClick,
}: CardToggleButtonProps<T>) {
  if (variant === 'horizontal') {
    return (
      <button
        type="button"
        role="radio"
        aria-checked={active}
        disabled={disabled}
        className={cn(cardToggleVariants({ variant, colorScheme, active }))}
        onClick={() => onClick(option.value)}
      >
        <Icon icon={option.icon} size={18} className={active ? 'text-primary' : 'text-muted-foreground'} />
        <div>
          <p className="text-sm font-medium">{option.label}</p>
          {option.description && (
            <p className="text-xs text-muted-foreground">{option.description}</p>
          )}
        </div>
      </button>
    );
  }

  if (variant === 'vertical') {
    return (
      <button
        type="button"
        role="radio"
        aria-checked={active}
        disabled={disabled}
        className={cn(cardToggleVariants({ variant, colorScheme, active }))}
        onClick={() => onClick(option.value)}
      >
        <Icon icon={option.icon} size={16} />
        {option.label}
      </button>
    );
  }

  // vertical-prominent
  return (
    <button
      type="button"
      role="radio"
      aria-checked={active}
      disabled={disabled}
      className={cn(cardToggleVariants({ variant, colorScheme, active }))}
      onClick={() => onClick(option.value)}
    >
      {/* Checkmark badge */}
      {active && (
        <div className="absolute -top-1.5 -right-1.5 flex h-5 w-5 items-center justify-center rounded-full bg-accent shadow-sm">
          <Icon icon={IconKey.Check} size={12} className="text-accent-foreground" />
        </div>
      )}

      <div
        className={cn(
          'flex h-9 w-9 items-center justify-center rounded-full',
          active ? 'bg-accent/20' : 'bg-muted',
        )}
      >
        <Icon icon={option.icon} size={16} className={cn(active ? 'text-accent' : 'text-muted-foreground')} />
      </div>
      <span className={cn('text-xs font-semibold', active && 'text-accent')}>
        {option.label}
      </span>
      {option.description && (
        <span className="text-[10px] text-muted-foreground leading-tight">
          {option.description}
        </span>
      )}
    </button>
  );
}

// ─── Main component ────────────────────────────────────────────

export function FieldCardToggle<T extends string>({
  label,
  description,
  errors,
  required,
  disabled,
  options,
  value,
  onValueChange,
  variant = 'horizontal',
  colorScheme = 'primary',
  columns,
  className,
}: FieldCardToggleProps<T>) {
  const cols = columns ?? options.length;

  return (
    <Field className={className}>
      {label && (
        <FieldLabel className="gap-1">
          {label} {required && <span className="text-destructive">*</span>}
        </FieldLabel>
      )}

      <div role="radiogroup" className={cn('grid gap-2', gridColsClass(cols))}>
        {options.map((option) => (
          <CardToggleButton
            key={option.value}
            option={option}
            active={value === option.value}
            disabled={disabled}
            variant={variant}
            colorScheme={colorScheme}
            onClick={onValueChange}
          />
        ))}
      </div>

      {description && <FieldDescription>{description}</FieldDescription>}
      <FieldError errors={errors} />
    </Field>
  );
}
